// The pickup card (James, 2026-10-23: "I can't tell what they are because they don't have any
// popups"). Walk up to an ordnance crate or a power-up crystal and a visor card names it, says
// what it does, and says what taking it needs: walk over it, hold □ to swap out a loaded special,
// or use the power-up you're already holding. Take it and the card flips to ACQUIRED with the
// button that fires it. The card is drawn by hud.ts (drawPickup); this file only decides what's on it.

import type { HudPickup } from '../render/contracts';
import { Ev, type GameEvent } from '../../sim/codec';
import { PlayerState } from '../../sim/player';
import { PU, PUEv, PU_DEFS, puColor, puLabel, ELEM_COLOR, PICKUP_R } from '../../sim/powerups';
import { Canopy, Elem, NO_WEAPON, WSlot, weaponDef } from '../../sim/weapons';
import { CRATE_R } from '../../sim/arsenal';
import { BIND } from './hud';

/** the card opens this far from a drop (m); the world marker carries it further out */
export const CARD_RANGE = 7;
/** how long the ACQUIRED card stays up (s) */
export const GOT_SECS = 3.4;

/** the glyphs the card's action row uses, per input (CONTROLS.md: □ grabs, D-pad ◀ the special, ▼ the power-up) */
export const PICKUP_BIND = {
  pad: { special: BIND.special.pad, powerup: BIND.powerup.pad, interact: '□' },
  kbm: { special: BIND.special.kbm, powerup: BIND.powerup.kbm, interact: 'E' },
} as const;

const css = (hex: number) => `#${hex.toString(16).padStart(6, '0')}`;
const ELEM_HEX: Record<number, number> = { ...ELEM_COLOR, [Elem.Magnetic]: 0xb85aff, [Elem.Rainbow]: 0xfff2d6 };
const CANOPY_HEX = [0xff7a2e, 0x8fdcff, 0xffe94d, 0x9bff3c, 0xb85aff, 0xffd959, 0x73e6ff, 0x59f273];
const ORDNANCE_HEX = 0xffb347;

/** an ordnance special's accent: its element, its canopy's rain, or the crate's amber */
export function crateColor(wid: number) {
  const w = weaponDef(wid);
  if (w.payload === 2) return css(CANOPY_HEX[w.canopy as Canopy] ?? ORDNANCE_HEX);
  return css(ELEM_HEX[w.elem] ?? ORDNANCE_HEX);
}

const ELEM_WORD: Record<number, string> = { [Elem.Fire]: 'Fire', [Elem.Ice]: 'Ice', [Elem.Shock]: 'Shock', [Elem.Acid]: 'Acid', [Elem.Magnetic]: 'Magnetic', [Elem.Rainbow]: 'All five elements' };
/** a special's description, leading with what its element does (the shared blurbs don't say) */
export function crateBlurb(wid: number) {
  const w = weaponDef(wid);
  if (w.payload === 2 && w.sub.includes(':')) { const r = w.sub.split(':')[1].trim(); return `${r.charAt(0).toUpperCase()}${r.slice(1)}. ${w.blurb}`; }
  if (w.payload === 1 && w.elem !== Elem.None) return `${ELEM_WORD[w.elem] ?? ''} seekers. ${w.blurb}`;
  if (w.elem !== Elem.None && w.sub) return `${w.sub}. ${w.blurb}`;
  return w.blurb;
}

/** "DIRECT HITS DO 30 %" reads better small as "Direct hits do 30 %" */
const sentence = (s: string) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : s);

interface Crateish { x: number; y: number; z: number; wid: number }
interface Pickupish { type: number; el: number; x: number; y: number; z: number }

export class PickupCard {
  private out: HudPickup = { kind: 'crate', mode: 'near', title: '', tag: '', blurb: '', color: '', glyph: '', verb: '', rest: '', dist: 0, age: 0, fade: 1 };
  private got: { kind: 'crate' | 'powerup'; wid: number; type: number; el: number; at: number; second: boolean } | null = null;
  private key = '';
  private keyAt = 0;
  private last: HudPickup | null = null;
  private lastAt = -9;

  /** your own takes flip the card to ACQUIRED */
  onEvent(e: GameEvent, myId: number, time: number, me: PlayerState | null) {
    const f = e.f;
    if (e.t === Ev.Crate && f[5] === 0 && f[6] === myId) this.got = { kind: 'crate', wid: f[1], type: 0, el: -1, at: time, second: false };
    else if (e.t === Ev.PowerUp && f[0] === myId && f[3] === PUEv.Picked) {
      // into the second slot when the first was already full
      const second = !!me && me.pu !== PU.None && me.pu !== f[1];
      this.got = { kind: 'powerup', wid: 0, type: f[1], el: f[2] - 1, at: time, second };
    }
  }

  reset() { this.got = null; this.last = null; this.key = ''; }

  /** what the card shows this frame (null: nothing near and nothing just taken) */
  update(time: number, me: PlayerState | null, crates: Iterable<Crateish>, pickups: readonly Pickupish[], bind: { special: string; powerup: string; interact: string }): HudPickup | null {
    const o = this.out;
    let on = false;
    if (me && me.phase === 0) {
      if (this.got && time - this.got.at < GOT_SECS) {
        const g = this.got;
        o.mode = 'got'; o.kind = g.kind; o.dist = 0;
        if (g.kind === 'crate') {
          const w = weaponDef(g.wid);
          o.title = w.name.toUpperCase(); o.tag = `ACQUIRED · ORDNANCE · ${w.role}`; o.blurb = crateBlurb(g.wid); o.color = crateColor(g.wid);
          o.glyph = bind.special; o.verb = 'ARM IT'; o.rest = w.mag > 1 ? ` · THEN FIRE · ${w.mag} SHOTS` : ' · THEN FIRE · ONE SHOT';
        } else {
          const d = PU_DEFS[g.type];
          o.title = puLabel(g.type, g.el); o.tag = `ACQUIRED · POWER-UP · ${d?.secs ? `${d.secs} S` : 'INSTANT'}`;
          o.blurb = sentence(d?.blurb ?? ''); o.color = css(puColor(g.type, g.el));
          o.glyph = bind.powerup;
          o.verb = g.second ? 'QUEUED' : 'USE IT'; o.rest = g.second ? ' · SECOND SLOT, AFTER THE ONE YOU HOLD' : ' · WHENEVER YOU LIKE';
        }
        on = true;
      } else {
        this.got = null;
        // the nearest drop in reach of the card
        let bd = CARD_RANGE, crate: Crateish | null = null, pu: Pickupish | null = null;
        for (const c of crates) { const d = Math.hypot(c.x - me.x, c.z - me.z); if (d < bd && Math.abs(c.y - me.y) < 8) { bd = d; crate = c; } }
        for (const k of pickups) { const d = Math.hypot(k.x - me.x, k.z - me.z); if (d < bd && Math.abs(k.y - me.y) < 8) { bd = d; pu = k; crate = null; } }
        if (crate) {
          const w = weaponDef(crate.wid);
          o.mode = 'near'; o.kind = 'crate'; o.dist = bd;
          o.title = w.name.toUpperCase(); o.tag = `ORDNANCE CRATE · ${w.role}`; o.blurb = crateBlurb(crate.wid); o.color = crateColor(crate.wid);
          const loaded = me.w2 !== NO_WEAPON && (me.wSlot === WSlot.Special ? me.mag : me.mag2) > 0;
          if (loaded) { o.glyph = bind.interact; o.verb = 'HOLD TO SWAP'; o.rest = ` · YOUR ${weaponDef(me.w2).short} GOES`; }
          else { o.glyph = ''; o.verb = bd <= CRATE_R ? 'TAKING IT' : 'WALK OVER IT'; o.rest = bd <= CRATE_R ? '' : ' TO TAKE IT'; }
          on = true;
        } else if (pu) {
          const d = PU_DEFS[pu.type];
          o.mode = 'near'; o.kind = 'powerup'; o.dist = bd;
          o.title = puLabel(pu.type, pu.el); o.tag = `POWER-UP · ${d?.secs ? `${d.secs} S` : 'INSTANT'}`;
          o.blurb = sentence(d?.blurb ?? ''); o.color = css(puColor(pu.type, pu.el));
          const full = me.pu !== PU.None && (me.puCap < 2 || me.pu2 !== PU.None);
          if (full) { o.glyph = bind.powerup; o.verb = `USE ${puLabel(me.pu, me.puEl - 1)}`; o.rest = ' FIRST · YOUR SLOT IS FULL'; }
          else { o.glyph = ''; o.verb = bd <= PICKUP_R ? 'TAKING IT' : 'WALK OVER IT'; o.rest = bd <= PICKUP_R ? '' : ' TO TAKE IT'; }
          on = true;
        }
      }
    }
    if (on) {
      // the card's age restarts when it shows something new (its open animation)
      const k = `${o.mode}|${o.kind}|${o.title}`;
      if (k !== this.key) { this.key = k; this.keyAt = time; }
      o.age = time - this.keyAt;
      o.fade = o.mode === 'got' ? Math.min(1, Math.max(0, (GOT_SECS - o.age) / 0.45)) : 1;
      this.last = o; this.lastAt = time;
      return o;
    }
    // walked away: the last card fades out over a quarter second
    this.key = '';
    if (this.last && time - this.lastAt < 0.25) { this.last.fade = 1 - (time - this.lastAt) / 0.25; return this.last; }
    this.last = null;
    return null;
  }
}
